import theme from "@shared/utils/themeColors";
import React from "react";
import { useTutorContext } from "../context/TutorContext";
import "../styles/tutor-transitions.css";
import TutorActiveConversation from "./TutorActiveConversation";
import TutorConversationList from "./TutorConversationList";

/**
 * Ask a Tutor chat window component
 * Used by the floating bubble
 * Handles transitions between conversation list and active conversation
 */
const TutorChatWindow: React.FC = () => {
  const {
    closeChat,
    selectedConversationId,
    setSelectedConversationId,
    currentView,
    setCurrentView,
    fetchConversations,
  } = useTutorContext();

  // Handle back button click
  const handleBackToList = async () => {
    // Fetch latest conversations before showing the list
    try {
      await fetchConversations();
    } catch (error) {
      console.error("Error refreshing conversations:", error);
    }

    // Switch to list view
    setCurrentView("list");
  };

  // Handle selecting a conversation
  const handleSelectConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setCurrentView("conversation");
  };

  return (
    <div className="flex flex-col w-[400px] h-[600px] max-w-full max-h-full bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-300 dark:border-gray-700 overflow-hidden">
      {/* Chat header */}
      <div className={`flex items-center p-4 border-b ${theme.border.primary}`}>
        <div className="flex items-center">
          {currentView === "conversation" && (
            <button
              onClick={handleBackToList}
              className={`mr-2 ${theme.text.mutedHover}`}
              aria-label="Back to conversations"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div className="flex-shrink-0 mr-3">
            <img
              src="/assets/images/avatar.png"
              alt="Ask a Tutor"
              className="w-9 h-9 rounded-full"
            />
          </div>
          <div>
            <h3 className={`font-semibold ${theme.text.primary}`}>
              Ask a Tutor
            </h3>
            <p className={`text-xs ${theme.text.muted}`}>
              {currentView === "list"
                ? "Select a conversation or start a new one"
                : "How can I help with your questions?"}
            </p>
          </div>
        </div>

        <button
          onClick={closeChat}
          className={`ml-auto ${theme.text.mutedHover}`}
          aria-label="Close chat"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="flex-grow flex flex-col overflow-hidden relative">
        <div
          className={`absolute inset-0 transition-all duration-300 ease-in-out ${
            currentView === "list"
              ? "translate-x-0 opacity-100 z-10"
              : "-translate-x-full opacity-0 z-0"
          }`}
        >
          <TutorConversationList
            onSelectConversation={handleSelectConversation}
          />
        </div>

        <div
          className={`absolute inset-0 transition-all duration-300 ease-in-out ${
            currentView === "conversation"
              ? "translate-x-0 opacity-100 z-10"
              : "translate-x-full opacity-0 z-0"
          }`}
        >
          <TutorActiveConversation conversationId={selectedConversationId} />
        </div>
      </div>
    </div>
  );
};

export default TutorChatWindow;
